import type { UniqueIdentifier } from '@dnd-kit/core';
import { observer } from 'mobx-react-lite';
import type { Dispatch, JSX, SetStateAction } from 'react';
import React from 'react';

import type { Form, IRowItem } from '@feathr/blackbox';

import type { IFieldError } from '../FormEditor';
import FieldConfiguration from './FieldConfiguration';
import FieldProperties from './FieldProperties';
import SubmitButtonProperties from './SubmitButtonProperties';

import * as styles from './ConfigurationPanel.css';

interface IProps {
  focusedField: IRowItem | undefined;
  form: Form;
  fields: IRowItem[];
  isDragging: boolean;
  isDraggingFromBuilder: boolean;
  isSubmitButtonFocused: boolean;
  onFocusField: Dispatch<SetStateAction<IRowItem | undefined>>;
  updateFieldProperties: (key: UniqueIdentifier, value: unknown) => void;
  onDeleteField: () => void;
  onFieldBlur: (fieldId: string) => void;
  validationErrors: IFieldError[];
  onFocusSubmitButton: Dispatch<SetStateAction<boolean>>;
}

function ConfigurationPanel({
  fields,
  form,
  focusedField,
  isDragging,
  isDraggingFromBuilder,
  isSubmitButtonFocused,
  onFocusField,
  updateFieldProperties,
  onDeleteField,
  onFieldBlur,
  validationErrors,
  onFocusSubmitButton,
}: IProps): JSX.Element {
  function handleBlur(event: React.FocusEvent<HTMLDivElement>): void {
    // Unfocus if user clicks outside of the panel
    if (event.relatedTarget === null) {
      onFocusField(undefined);
    }
  }

  // While a drag is active, always show the droppable field list so a dragged field can be dropped
  // back; otherwise show the submit button or field properties when one is focused.
  let content = (
    <FieldConfiguration fields={fields} form={form} isDraggingFromBuilder={isDraggingFromBuilder} />
  );
  if (!isDragging && isSubmitButtonFocused) {
    content = <SubmitButtonProperties form={form} toggleFocus={onFocusSubmitButton} />;
  } else if (!isDragging && focusedField) {
    content = (
      <FieldProperties
        field={focusedField}
        isRemovable={focusedField.id !== 'email'}
        onDeleteField={onDeleteField}
        onFieldBlur={onFieldBlur}
        onFocusField={onFocusField}
        updateFieldProperties={updateFieldProperties}
        validationErrors={validationErrors}
      />
    );
  }

  return (
    // Make div focusable so it does not change the focused state of the active input
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <div className={styles.root} onBlur={handleBlur} tabIndex={0}>
      {content}
    </div>
  );
}

export default observer(ConfigurationPanel);
